import { Button } from './ui/button';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

// Refined Static Workflow Diagram - More architectural, less tech-flashy
const WorkflowDiagram = () => (
  <svg
    viewBox="0 0 480 360"
    className="w-full h-full"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Subtle grid */}
    <defs>
      <pattern id="gridPattern" width="48" height="48" patternUnits="userSpaceOnUse">
        <path d="M 48 0 L 0 0 0 48" fill="none" stroke="#1e293b" strokeWidth="0.5" opacity="0.4" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#gridPattern)" />

    {/* Flow lines - clean and architectural */}
    <path d="M 90 180 L 150 180" stroke="#475569" strokeWidth="1.5" />
    <path d="M 330 180 L 390 180" stroke="#475569" strokeWidth="1.5" />
    <path d="M 240 130 L 240 80" stroke="#475569" strokeWidth="1" opacity="0.6" />
    <path d="M 240 230 L 240 280" stroke="#475569" strokeWidth="1" opacity="0.6" />
    
    {/* Arrow heads */}
    <polygon points="150,180 144,176 144,184" fill="#475569" />
    <polygon points="390,180 384,176 384,184" fill="#475569" />

    {/* Inbound Sources - Left side */}
    <g>
      <rect x="20" y="120" width="70" height="30" rx="1" fill="transparent" stroke="#334155" strokeWidth="1" />
      <text x="55" y="139" textAnchor="middle" fill="#64748b" fontSize="9" fontFamily="Inter">Requests</text>
    </g>
    <g>
      <rect x="20" y="165" width="70" height="30" rx="1" fill="transparent" stroke="#334155" strokeWidth="1" />
      <text x="55" y="184" textAnchor="middle" fill="#64748b" fontSize="9" fontFamily="Inter">Forms</text>
    </g>
    <g>
      <rect x="20" y="210" width="70" height="30" rx="1" fill="transparent" stroke="#334155" strokeWidth="1" />
      <text x="55" y="229" textAnchor="middle" fill="#64748b" fontSize="9" fontFamily="Inter">Messages</text>
    </g>

    {/* Central Processing Block */}
    <rect x="150" y="120" width="180" height="120" rx="2" fill="#0f172a" stroke="#334155" strokeWidth="1" />
    <rect x="158" y="128" width="164" height="104" rx="1" fill="none" stroke="#1e293b" strokeWidth="1" />
    
    {/* Internal structure visualization */}
    <text x="240" y="155" textAnchor="middle" fill="#94a3b8" fontSize="9" fontFamily="Inter" fontWeight="500" letterSpacing="0.05em">STRUCTURED AUTOMATION</text>
    <line x1="175" y1="168" x2="305" y2="168" stroke="#1e293b" strokeWidth="1" />
    
    <text x="240" y="190" textAnchor="middle" fill="#f1f5f9" fontSize="12" fontFamily="Manrope" fontWeight="600">Intake • Triage • Route</text>
    <text x="240" y="210" textAnchor="middle" fill="#64748b" fontSize="10" fontFamily="Inter">Supervision & Control</text>

    {/* Structured Outputs - Right side */}
    <g>
      <rect x="390" y="120" width="70" height="30" rx="1" fill="transparent" stroke="#334155" strokeWidth="1" />
      <text x="425" y="139" textAnchor="middle" fill="#64748b" fontSize="9" fontFamily="Inter">Routed</text>
    </g>
    <g>
      <rect x="390" y="165" width="70" height="30" rx="1" fill="transparent" stroke="#334155" strokeWidth="1" />
      <text x="425" y="184" textAnchor="middle" fill="#64748b" fontSize="9" fontFamily="Inter">Actioned</text>
    </g>
    <g>
      <rect x="390" y="210" width="70" height="30" rx="1" fill="transparent" stroke="#334155" strokeWidth="1" />
      <text x="425" y="229" textAnchor="middle" fill="#64748b" fontSize="9" fontFamily="Inter">Monitored</text>
    </g>

    {/* Top label - Control layer */}
    <text x="240" y="65" textAnchor="middle" fill="#475569" fontSize="8" fontFamily="Inter" fontWeight="500" letterSpacing="0.1em">OPERATIONAL VISIBILITY</text>

    {/* Bottom label - Feedback */}
    <text x="240" y="300" textAnchor="middle" fill="#475569" fontSize="8" fontFamily="Inter" fontWeight="500" letterSpacing="0.1em">CONTINUOUS REFINEMENT</text>
  </svg>
);

export const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100vh] bg-slate-950 overflow-hidden"
    >
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
      }} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center min-h-[75vh]">
          {/* Left: Text Content - 7 columns */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="lg:col-span-7"
          >
            <h1
              data-testid="hero-headline"
              className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold tracking-tight leading-[1.08] text-white mb-8 font-['Manrope']"
            >
              Structured AI automation for operations-driven companies.
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-400 mb-6 max-w-2xl">
              Pyrtex builds intake systems, workflow automation, and supervisory control layers that help businesses handle inbound complexity with consistency and visibility.
            </p>
            <p className="text-base leading-relaxed text-slate-500 mb-10 max-w-2xl">
              For recruitment agencies, logistics operations, manufacturing environments, and any organization where structured process execution matters.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => scrollToSection('#contact')}
                data-testid="hero-cta-primary"
                className="bg-white text-slate-900 hover:bg-slate-100 uppercase text-xs tracking-wider font-semibold px-8 py-6 rounded-sm"
              >
                Request a Consultation
                <ArrowRight className="ml-2" size={16} strokeWidth={1.5} />
              </Button>
              <Button
                onClick={() => scrollToSection('#use-cases')}
                data-testid="hero-cta-secondary"
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white hover:border-slate-600 uppercase text-xs tracking-wider font-semibold px-8 py-6 rounded-sm"
              >
                See Use Cases
              </Button>
            </div>
          </motion.div>

          {/* Right: Workflow Diagram - 5 columns */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
            className="hidden lg:block lg:col-span-5"
          >
            <WorkflowDiagram />
          </motion.div>
        </div>

        {/* Bottom: Industry indicators */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.4 }}
          className="pt-16 border-t border-slate-800/50"
        >
          <p className="text-xs uppercase tracking-[0.15em] text-slate-600 mb-6">
            Built for
          </p>
          <div className="flex flex-wrap gap-x-10 gap-y-3">
            <span className="text-sm text-slate-500 font-medium">Recruitment Operations</span>
            <span className="text-sm text-slate-500 font-medium">Logistics Coordination</span>
            <span className="text-sm text-slate-500 font-medium">Manufacturing Intake</span>
            <span className="text-sm text-slate-500 font-medium">Service Operations</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
